import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bookmark } from 'lucide-react';
import { recordGuestFunnelEvent } from '../lib/guestFunnel';
import '../styles/GuestSaveSpotBreadcrumb.css';

const SHIMMER_FIRST_KEY = 'cadence_guest_breadcrumb_shimmered';
const SHIMMER_75_KEY = 'cadence_guest_breadcrumb_shimmered_75';
const SECOND_SHIMMER_THRESHOLD = 75;

/**
 * Quiet, persistent floating bookmark for guest visitors taking the intake.
 *
 * Always visible (for guests, on /portal?tab=intake) — never auto-pops, never
 * gates the survey. Hover/tap reveals a luxe "Save your spot" copper-gold
 * tooltip; click navigates to /login?redirect=intake&mode=signup so they can
 * convert without losing their progress.
 *
 * Two one-shot shimmers per session:
 *  1. First-appearance shimmer — fires 900ms after first mount.
 *  2. Deep-progress shimmer — fires once when the guest crosses 75% (a "last
 *     chance to save" moment) IF they didn't sign up after the first nudge.
 *
 * Both shimmers are tracked separately in sessionStorage so they only fire
 * once per session each. After each shimmer, the pill collapses back to its
 * quiet 44px bookmark disc.
 *
 * Lives in bottom-left so it doesn't collide with the chat-with-us widget
 * (bottom-right) or the Emergent footer badge.
 */
const GuestSaveSpotBreadcrumb = ({ isGuest, isAuthenticated, progressPercent = 0 }) => {
  const navigate = useNavigate();
  const [hover, setHover] = useState(false);
  const [shimmer, setShimmer] = useState(false);

  // First-impression shimmer — 900ms after mount, once per session.
  useEffect(() => {
    if (isAuthenticated || !isGuest) return;
    recordGuestFunnelEvent('breadcrumb_mounted', progressPercent);
    if (sessionStorage.getItem(SHIMMER_FIRST_KEY) === '1') return;
    const fireTimer = setTimeout(() => {
      setShimmer(true);
      sessionStorage.setItem(SHIMMER_FIRST_KEY, '1');
      recordGuestFunnelEvent('breadcrumb_shimmer_1', progressPercent);
    }, 900);
    const stopTimer = setTimeout(() => setShimmer(false), 900 + 1400);
    return () => {
      clearTimeout(fireTimer);
      clearTimeout(stopTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuest, isAuthenticated]);

  // Deep-progress shimmer — fires once when crossing 75%.
  useEffect(() => {
    if (isAuthenticated || !isGuest) return;
    if (progressPercent < SECOND_SHIMMER_THRESHOLD) return;
    if (sessionStorage.getItem(SHIMMER_75_KEY) === '1') return;
    setShimmer(true);
    sessionStorage.setItem(SHIMMER_75_KEY, '1');
    recordGuestFunnelEvent('breadcrumb_shimmer_75', progressPercent);
    const stopTimer = setTimeout(() => setShimmer(false), 1400);
    return () => clearTimeout(stopTimer);
  }, [progressPercent, isGuest, isAuthenticated]);

  if (isAuthenticated || !isGuest) return null;

  const handleClick = () => {
    recordGuestFunnelEvent('breadcrumb_clicked', progressPercent);
    navigate('/login?redirect=intake&mode=signup');
  };

  return (
    <button
      type="button"
      className={[
        'savespot-breadcrumb',
        hover ? 'savespot-breadcrumb--open' : '',
        shimmer ? 'savespot-breadcrumb--shimmer' : '',
      ].filter(Boolean).join(' ')}
      onClick={handleClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      aria-label="Save your spot &mdash; create an account to keep your progress across devices"
      data-testid="guest-savespot-breadcrumb"
    >
      <span className="savespot-breadcrumb-icon">
        <Bookmark size={16} />
      </span>
      <span className="savespot-breadcrumb-label">Save your spot</span>
    </button>
  );
};

export default GuestSaveSpotBreadcrumb;
